import { GooglePayButtonWrapper } from '@/components/PaymentButtons/GooglePay';
import ApplePayButtonWrapper from '@/components/PaymentButtons/ApplePay';
import { useGooglePayConfig } from '@/hooks/useGooglePayConfig';
import { useApplePaySettings } from '@/contexts/applePayContext';
import { usePageData } from '@/contexts/pageContext';
import { parsePrice } from '@/utils/googlePayPricing';
import * as Styled from './QuickPay.styled';

interface QuickPayProps {
  isBuyNow?: boolean;
  productCode?: string;
  quantity?: number;
}

// Safe hook to use checkout context only if available
const useSafeCheckout = () => {
  try {
    // Import and use checkout context - will throw if not available
    const { useCheckout } = require('@/contexts/checkoutContext');
    return useCheckout();
  } catch (error) {
    // CheckoutProvider not available, return mock data
    console.log('QuickPay: CheckoutProvider not available, using fallback');
    return { 
      checkoutData: { 
        amounts: { total: null } 
      } 
    };
  }
};

/**
 * QuickPay component that hosts both Google Pay and Apple Pay buttons
 * Each payment method handles its own order creation and error handling
 */
export const QuickPay = ({ isBuyNow = false, productCode, quantity = 1 }: QuickPayProps) => {
    const { config: googlePaySettings, loading: googlePayLoading, error: googlePayError } = useGooglePayConfig();
    const { settings: applePaySettings, loading: applePayLoading } = useApplePaySettings();
    const { product, termsConditions } = usePageData();
    const { checkoutData } = useSafeCheckout();

    console.log('QuickPay: Props received', { isBuyNow, productCode, quantity });
    console.log('QuickPay: Product data', { product, price: product?.price, current: product?.price?.current });
    console.log('QuickPay: Checkout data available', { hasCheckoutData: !!checkoutData, amounts: checkoutData?.amounts });

    // Check if payment is required (total amount > 0)
    const totalAmount = checkoutData?.amounts?.total ? parsePrice(checkoutData.amounts.total) : null;
    const isPaymentRequired = isBuyNow
        ? true // Always show for Buy Now since no gift cards can be applied
        : totalAmount !== null
            ? totalAmount > 0
            : true; // Default to showing if we don't have checkout data yet

    const isLoading = googlePayLoading || applePayLoading;

    if (isLoading) {
        return <div>Loading payment options...</div>;
    }

    if (googlePayError && !applePaySettings) {
        console.error('QuickPay: Error loading configurations:', { googlePayError, applePaySettings });
        return null;
    }

    if (!googlePaySettings && !applePaySettings) {
        console.error('QuickPay: No configuration data available');
        return null;
    }

    // Don't show payment buttons if gift/loyalty cards cover the full amount
    if (!isPaymentRequired) {
        console.log('QuickPay: Payment not required, order fully covered by gift/loyalty cards');
        return null;
    }

    // Only render payment buttons that are enabled
    return (
        <Styled.QuickPayContainer>
            {googlePaySettings?.enabled && (
                <GooglePayButtonWrapper
                    style="PaymentOptions"
                    buttonId={isBuyNow ? "google-pay-buyNow" : "google-pay-paymentOptions"}
                    googlePaySettings={googlePaySettings}
                    isBuyNow={isBuyNow}
                    productCode={productCode}
                    quantity={quantity}
                    productPrice={product?.price?.current}
                    productOriginalPrice={product?.price?.former}
                />
            )}
            {applePaySettings?.enabled && (
                <ApplePayButtonWrapper
                    style="PaymentOptions"
                    buttonId={isBuyNow ? "apple-pay-buyNow" : "apple-pay-paymentOptions"}
                    isBuyNow={isBuyNow}
                    productCode={isBuyNow ? productCode : undefined}
                />
            )}
            {(applePaySettings?.enabled || googlePaySettings?.enabled) && (
                <Styled.QuickPayTermsConditions
                    // biome-ignore lint/security/noDangerouslySetInnerHtml: we trust the source of the HTML
                    dangerouslySetInnerHTML={{
                        __html: termsConditions
                            ? termsConditions
                            : '<p>By placing your order, you confirm you have read and accepted our terms & conditions and privacy policy</p>',
                    }}
                />
          )}
         </Styled.QuickPayContainer>
  );
}